import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import IngredientAutocomplete from './IngredientAutocomplete';
import {
    Sparkles,
    DollarSign,
    Clock,
    Users,
    ChefHat,
    Loader2,
    Check,
    X,
    ArrowRight,
    AlertCircle,
    ShoppingCart,
    Package
} from 'lucide-react';

const PersonalPackGenerator = ({ onPackGenerated, onClose }) => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [generatedPack, setGeneratedPack] = useState(null);
    
    // Form state
    const [criteria, setCriteria] = useState({
        budget_type: 'normal',
        household_size: 'family',
        max_time: 60,
        preferences: [],
        excluded_ingredients: [],
        prioritize_ingredients: []
    });
    
    // Temp inputs
    const [preferenceInput, setPreferenceInput] = useState('');
    const [excludedInput, setExcludedInput] = useState('');

    const budgetOptions = [
        { value: 'tight', label: 'Fin de mois', description: '≤2.50$/portion', icon: '💰', color: 'border-red-300 bg-red-50 hover:bg-red-100' },
        { value: 'normal', label: 'Normal', description: '≤5$/portion', icon: '🍽️', color: 'border-blue-300 bg-blue-50 hover:bg-blue-100' },
        { value: 'comfortable', label: 'Confortable', description: 'Sans limite', icon: '✨', color: 'border-purple-300 bg-purple-50 hover:bg-purple-100' }
    ];

    const householdOptions = [
        { value: 'solo', label: 'Solo', description: '1 personne', icon: '👤' },
        { value: 'couple', label: 'Couple', description: '2 personnes', icon: '👫' },
        { value: 'family', label: 'Famille', description: '4-5 personnes', icon: '👨‍👩‍👧‍👦' },
        { value: 'large_family', label: 'Grande famille', description: '6+ personnes', icon: '👨‍👩‍👧‍👦👶' }
    ];

    const timeOptions = [
        { value: 30, label: 'Express', description: '≤30 min' },
        { value: 45, label: 'Rapide', description: '≤45 min' },
        { value: 60, label: 'Normal', description: '≤60 min' },
        { value: 90, label: 'Élaboré', description: '≤90 min' }
    ];

    const commonPreferences = [
        'Végétarien', 'Végane', 'Sans gluten', 'Sans lactose', 
        'Peu épicé', 'Riche en protéines', 'Faible en glucides'
    ];

    const addPreference = (pref) => {
        if (pref && !criteria.preferences.includes(pref)) {
            setCriteria(prev => ({
                ...prev,
                preferences: [...prev.preferences, pref]
            }));
        }
        setPreferenceInput('');
    };

    const removePreference = (pref) => {
        setCriteria(prev => ({
            ...prev,
            preferences: prev.preferences.filter(p => p !== pref)
        }));
    };

    const addExcluded = (item) => {
        if (item && !criteria.excluded_ingredients.includes(item)) {
            setCriteria(prev => ({
                ...prev,
                excluded_ingredients: [...prev.excluded_ingredients, item.toLowerCase()]
            }));
        }
        setExcludedInput('');
    };

    const removeExcluded = (item) => {
        setCriteria(prev => ({
            ...prev,
            excluded_ingredients: prev.excluded_ingredients.filter(i => i !== item)
        }));
    };

    const generatePack = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.generatePersonalPack(criteria);
            setGeneratedPack(response.data);
            setStep(4); // Show results
            if (onPackGenerated) onPackGenerated(response.data);
        } catch (err) {
            console.error('Error generating pack:', err);
            setError(err.response?.data?.detail || 'Erreur lors de la génération du pack');
        } finally {
            setLoading(false);
        }
    };

    const renderStep1 = () => (
        <div className="space-y-6">
            <div>
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    Quel est ton budget?
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {budgetOptions.map(opt => (
                        <button
                            key={opt.value}
                            onClick={() => setCriteria(prev => ({ ...prev, budget_type: opt.value }))}
                            className={`p-4 rounded-xl border-2 transition-all text-left ${
                                criteria.budget_type === opt.value
                                    ? 'border-foreground ring-2 ring-primary/30 shadow-lg'
                                    : opt.color
                            }`}
                            data-testid={`budget-${opt.value}`}
                        >
                            <div className="text-2xl mb-1">{opt.icon}</div>
                            <div className="font-bold">{opt.label}</div>
                            <div className="text-sm text-muted-foreground">{opt.description}</div>
                            {criteria.budget_type === opt.value && (
                                <Check className="w-5 h-5 text-primary mt-2" />
                            )}
                        </button>
                    ))}
                </div>
            </div>

            <div>
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Pour combien de personnes?
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {householdOptions.map(opt => (
                        <button
                            key={opt.value}
                            onClick={() => setCriteria(prev => ({ ...prev, household_size: opt.value }))}
                            className={`p-3 rounded-xl border-2 transition-all text-center ${
                                criteria.household_size === opt.value
                                    ? 'border-foreground bg-primary/10 ring-2 ring-primary/30'
                                    : 'border-gray-200 hover:bg-gray-50'
                            }`}
                            data-testid={`household-${opt.value}`}
                        >
                            <div className="text-xl">{opt.icon}</div>
                            <div className="font-medium text-sm">{opt.label}</div>
                            <div className="text-xs text-muted-foreground">{opt.description}</div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex justify-end">
                <Button onClick={() => setStep(2)} className="gap-2" data-testid="next-step-1">
                    Suivant <ArrowRight className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="space-y-6">
            <div>
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-orange-600" />
                    Temps de préparation max par repas?
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {timeOptions.map(opt => (
                        <button
                            key={opt.value}
                            onClick={() => setCriteria(prev => ({ ...prev, max_time: opt.value }))}
                            className={`p-4 rounded-xl border-2 transition-all ${
                                criteria.max_time === opt.value
                                    ? 'border-foreground bg-orange-50 ring-2 ring-orange-300'
                                    : 'border-gray-200 hover:bg-gray-50'
                            }`}
                            data-testid={`time-${opt.value}`}
                        >
                            <div className="font-bold">{opt.label}</div>
                            <div className="text-sm text-muted-foreground">{opt.description}</div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(1)}>
                    Retour
                </Button>
                <Button onClick={() => setStep(3)} className="gap-2" data-testid="next-step-2">
                    Suivant <ArrowRight className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );

    const renderStep3 = () => (
        <div className="space-y-6">
            <div>
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                    <ChefHat className="w-5 h-5 text-purple-600" />
                    Préférences alimentaires (optionnel)
                </h3>
                <div className="flex flex-wrap gap-2 mb-3">
                    {commonPreferences.map(pref => (
                        <button
                            key={pref}
                            onClick={() => addPreference(pref)}
                            disabled={criteria.preferences.includes(pref)}
                            className={`px-3 py-1 rounded-full text-sm border transition-all ${
                                criteria.preferences.includes(pref)
                                    ? 'bg-primary text-primary-foreground border-primary'
                                    : 'border-gray-300 hover:bg-gray-100'
                            }`}
                        >
                            {pref}
                        </button>
                    ))}
                </div>
                {criteria.preferences.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 bg-purple-50 rounded-lg">
                        {criteria.preferences.map(pref => (
                            <Badge key={pref} variant="secondary" className="gap-1">
                                {pref}
                                <X 
                                    className="w-3 h-3 cursor-pointer hover:text-red-500" 
                                    onClick={() => removePreference(pref)}
                                />
                            </Badge>
                        ))}
                    </div>
                )}
            </div>

            <div>
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    Ingrédients à éviter (optionnel)
                </h3>
                <div className="flex gap-2 mb-3">
                    <input
                        type="text"
                        value={excludedInput}
                        onChange={(e) => setExcludedInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addExcluded(excludedInput)}
                        placeholder="Ex: fruits de mer, arachides..."
                        className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    />
                    <Button 
                        variant="outline" 
                        onClick={() => addExcluded(excludedInput)}
                        disabled={!excludedInput.trim()}
                    >
                        Ajouter
                    </Button>
                </div>
                {criteria.excluded_ingredients.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 bg-red-50 rounded-lg">
                        {criteria.excluded_ingredients.map(item => (
                            <Badge key={item} variant="destructive" className="gap-1">
                                {item}
                                <X 
                                    className="w-3 h-3 cursor-pointer" 
                                    onClick={() => removeExcluded(item)}
                                />
                            </Badge>
                        ))}
                    </div>
                )}
            </div>

            {/* NEW: Ingredients to prioritize */}
            <div>
                <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                    <Package className="w-5 h-5 text-green-600" />
                    J'ai ces ingrédients à la maison (optionnel)
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                    L'IA privilégiera les recettes qui utilisent ces ingrédients
                </p>
                <IngredientAutocomplete
                    selectedIngredients={criteria.prioritize_ingredients}
                    onIngredientsChange={(ingredients) => setCriteria(prev => ({
                        ...prev,
                        prioritize_ingredients: ingredients
                    }))}
                    placeholder="Tapez un ingrédient (ex: poulet, riz, tomates...)"
                    maxSelections={8}
                />
            </div>

            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    {error}
                </div>
            )}

            <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(2)}>
                    Retour
                </Button>
                <Button 
                    onClick={generatePack} 
                    disabled={loading}
                    className="gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                    data-testid="generate-pack-btn"
                >
                    {loading ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            L'IA crée ton pack...
                        </>
                    ) : (
                        <>
                            <Sparkles className="w-4 h-4" />
                            Générer mon pack IA
                        </>
                    )}
                </Button>
            </div>
        </div>
    );

    const renderStep4 = () => (
        <div className="space-y-6">
            <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">
                    {generatedPack?.title || 'Ton pack est prêt!'}
                </h3>
                <p className="text-muted-foreground">
                    {generatedPack?.description}
                </p>
            </div>

            {/* Pack Summary */}
            <Card className="border-2 border-green-200 bg-green-50/50">
                <CardContent className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                        <div>
                            <div className="text-2xl font-bold text-green-600">
                                {generatedPack?.soupers?.length || 5}
                            </div>
                            <div className="text-sm text-muted-foreground">recettes</div>
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-blue-600">
                                ~{generatedPack?.estimated_total_cost?.toFixed(0) || '?'}$
                            </div>
                            <div className="text-sm text-muted-foreground">coût total</div>
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-purple-600">
                                {generatedPack?.grocery_list?.length || '?'}
                            </div>
                            <div className="text-sm text-muted-foreground">ingrédients</div>
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-orange-600">
                                ~{generatedPack?.estimated_cost_per_person?.toFixed(0) || '?'}$
                            </div>
                            <div className="text-sm text-muted-foreground">par personne</div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Weekly Tip */}
            {generatedPack?.weekly_tip && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="font-medium text-yellow-800 mb-1">💡 Conseil de la semaine</div>
                    <p className="text-yellow-700">{generatedPack.weekly_tip}</p>
                </div>
            )}

            {/* Recipe List */}
            <div>
                <h4 className="font-bold mb-3">Tes 5 soupers de la semaine:</h4>
                <div className="space-y-2">
                    {generatedPack?.soupers?.map((souper, idx) => (
                        <div 
                            key={idx}
                            className="flex items-center gap-3 p-3 bg-white rounded-lg border hover:shadow-md transition-shadow cursor-pointer"
                            onClick={() => navigate(`/recipes/${souper.recipe_id}`)}
                        >
                            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center font-bold text-primary">
                                {souper.day?.charAt(0) || idx + 1}
                            </div>
                            {souper.image_url && (
                                <img 
                                    src={souper.image_url} 
                                    alt={souper.title}
                                    className="w-12 h-12 rounded-lg object-cover"
                                />
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{souper.title}</div>
                                <div className="text-sm text-muted-foreground">
                                    {souper.day} • {souper.prep_time + souper.cook_time} min • {souper.cost_per_serving?.toFixed(2)}$/portion
                                </div>
                            </div>
                            <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex gap-3">
                <Button 
                    variant="outline" 
                    onClick={() => {
                        setStep(1);
                        setGeneratedPack(null);
                    }}
                    className="flex-1"
                >
                    Générer un autre pack
                </Button>
                <Button 
                    onClick={() => setStep(5)}
                    className="flex-1 gap-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                    data-testid="view-grocery-list-btn"
                >
                    <ShoppingCart className="w-4 h-4" />
                    Voir la liste d'épicerie
                </Button>
            </div>
        </div>
    );

    const renderStep5 = () => (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
                        <ShoppingCart className="w-5 h-5 text-green-600" />
                        Ta liste d'épicerie
                    </h3>
                    <p className="text-sm text-muted-foreground">
                        {generatedPack?.grocery_list?.length || 0} ingrédients • ~{generatedPack?.estimated_total_cost?.toFixed(0) || '?'}$ estimé
                    </p>
                </div>
                <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setStep(4)}
                    className="gap-1"
                >
                    ← Retour aux recettes
                </Button>
            </div>

            {/* Grocery List */}
            <div className="max-h-96 overflow-y-auto space-y-2 pr-2">
                {generatedPack?.grocery_list?.map((item, idx) => (
                    <div 
                        key={idx}
                        className="flex items-center justify-between p-3 bg-white rounded-xl border hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center text-green-700 font-bold text-sm">
                                {item.count > 1 ? item.count : '✓'}
                            </div>
                            <div>
                                <div className="font-medium text-foreground">{item.item}</div>
                                <div className="text-sm text-muted-foreground">
                                    {item.quantity} {item.unit}
                                    {item.count > 1 && ` • utilisé ${item.count}x`}
                                </div>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="font-bold text-green-600">
                                ~{item.estimated_cost?.toFixed(2) || '?'}$
                            </div>
                            {item.recipes && item.recipes.length > 0 && (
                                <div className="text-xs text-muted-foreground max-w-32 truncate">
                                    {item.recipes[0]}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Total */}
            <div className="p-4 bg-gradient-to-r from-green-100 to-emerald-100 rounded-xl border-2 border-green-300">
                <div className="flex justify-between items-center">
                    <span className="font-bold text-foreground">Total estimé</span>
                    <span className="text-2xl font-black text-green-600">
                        ~{generatedPack?.estimated_total_cost?.toFixed(2) || '?'}$
                    </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                    Soit ~{generatedPack?.estimated_cost_per_person?.toFixed(2) || '?'}$ par personne
                </p>
            </div>

            <div className="flex gap-3">
                <Button 
                    variant="outline" 
                    onClick={() => {
                        setStep(1);
                        setGeneratedPack(null);
                    }}
                    className="flex-1"
                >
                    Nouveau pack
                </Button>
                <Button 
                    onClick={onClose}
                    className="flex-1 gap-2"
                >
                    <Check className="w-4 h-4" />
                    Terminé
                </Button>
            </div>
        </div>
    );

    return (
        <Card className="border-2 border-purple-200 shadow-xl">
            <CardHeader className={`text-white rounded-t-lg ${
                step === 5 
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
                    : 'bg-gradient-to-r from-purple-500 to-pink-500'
            }`}>
                <CardTitle className="flex items-center gap-2">
                    {step === 5 ? (
                        <>
                            <ShoppingCart className="w-5 h-5" />
                            Liste d'épicerie personnalisée
                        </>
                    ) : (
                        <>
                            <Sparkles className="w-5 h-5" />
                            Créer mon pack personnalisé avec l'IA
                        </>
                    )}
                </CardTitle>
                {step < 4 && (
                    <div className="flex gap-2 mt-2">
                        {[1, 2, 3].map(s => (
                            <div 
                                key={s}
                                className={`h-2 flex-1 rounded-full transition-all ${
                                    s <= step ? 'bg-white' : 'bg-white/30'
                                }`}
                            />
                        ))}
                    </div>
                )}
            </CardHeader>
            <CardContent className="p-6">
                {step === 1 && renderStep1()}
                {step === 2 && renderStep2()}
                {step === 3 && renderStep3()}
                {step === 4 && renderStep4()}
                {step === 5 && renderStep5()}
            </CardContent>
        </Card>
    );
};

export default PersonalPackGenerator;
